import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import axiosClient from '../../../axios-client';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '@chakra-ui/icons';
import { FaPlay } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import MyPagination from '../../../components/MyPagination';
import { useStateContext } from '../../../context/ContextProvider';
import { useDebounce } from '../../../hooks/useDebounce';
import {
    Flex,
    Box,
    Button,
    InputGroup,
    InputLeftElement,
    Input,
    useColorModeValue,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    SimpleGrid,
    Card,
    CardBody,
    CardFooter,
    Image,
    Heading,
    Skeleton,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    AspectRatio,
    Badge,
    Icon,
    Stack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon
} from '@chakra-ui/react';


const CategorySection = ({
    category, tutos, cardBg, hoverBg, cardInnerBg, textColor,
    handleWatchVideo, t, getYouTubeId, setDescModalTitle,
    setDescModalContent, onDescOpen
}) => {
    const scrollContainerRef = useRef(null);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(0);

    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            const pages = Math.ceil(scrollWidth / clientWidth);
            setTotalPages(Math.max(1, pages));

            if (pages > 1) {
                const maxScroll = scrollWidth - clientWidth;
                if (maxScroll <= 0) {
                    setCurrentPage(0);
                    return;
                }
                let current = Math.round((scrollLeft / maxScroll) * (pages - 1));
                current = Math.max(0, Math.min(pages - 1, current));
                setCurrentPage(current);
            } else {
                setCurrentPage(0);
            }
        }
    }, []);

    useEffect(() => {
        handleScroll();
        window.addEventListener('resize', handleScroll);
        return () => window.removeEventListener('resize', handleScroll);
    }, [handleScroll, tutos]);

    const scrollToPage = (pageIndex) => {
        if (scrollContainerRef.current) {
            const { scrollWidth, clientWidth } = scrollContainerRef.current;
            const maxScroll = scrollWidth - clientWidth;
            if (maxScroll <= 0) return;
            const targetScrollLeft = totalPages > 1 ? (pageIndex / (totalPages - 1)) * maxScroll : 0;

            scrollContainerRef.current.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth'
            });
            setCurrentPage(pageIndex);
        }
    };

    return (
        <AccordionItem bg={cardBg} borderRadius="2xl" overflow="hidden" shadow="sm" mb={4} border="none">
            <AccordionButton p={4} _hover={{ bg: hoverBg }}>
                <Box flex="1" textAlign="left" fontWeight="bold" fontSize="lg">
                    {category} <Badge ml={2} colorScheme="purple">{tutos.length}</Badge>
                </Box>
                <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6} px={4}>
                <Flex
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    overflowX="auto"
                    gap={6}
                    pb={4}
                    css={{
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        scrollSnapType: 'x mandatory'
                    }}
                >
                    {tutos.map((tuto) => {
                        const videoId = getYouTubeId(tuto.youtube_link);
                        const thumbnailUrl = videoId
                            ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                            : `https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=600&auto=format&fit=crop`;

                        return (
                            <Card
                                key={tuto.id}
                                minW={{ base: "280px", md: "320px" }}
                                maxW={{ base: "280px", md: "320px" }}
                                bg={cardInnerBg}
                                borderRadius="xl"
                                overflow="hidden"
                                shadow="sm"
                                scrollSnapAlign="start"
                                transition="all 0.3s"
                                _hover={{ transform: 'translateY(-4px)', shadow: 'md' }}
                            >
                                <Box position="relative" cursor="pointer" onClick={() => handleWatchVideo(tuto.youtube_link, tuto.title)}>
                                    <Image
                                        src={thumbnailUrl}
                                        alt={tuto.title}
                                        h="160px"
                                        w="100%"
                                        objectFit="cover"
                                    />
                                    <Flex
                                        position="absolute"
                                        top="0"
                                        left="0"
                                        w="100%"
                                        h="100%"
                                        bg="blackAlpha.400"
                                        align="center"
                                        justify="center"
                                        opacity="0"
                                        transition="all 0.3s"
                                        _hover={{ opacity: 1 }}
                                    >
                                        <CirclePlayButton />
                                    </Flex>
                                </Box>
                                <CardBody pb={2} p={4}>
                                    <Heading size="sm" mb={2} isTruncated>
                                        {tuto.title}
                                    </Heading>
                                    <Text fontSize="xs" color={textColor} noOfLines={3}>
                                        {tuto.description || t("Aucune description disponible pour ce tutoriel.")}
                                    </Text>
                                    {tuto.description && (
                                        <Button
                                            size="xs"
                                            variant="link"
                                            colorScheme="purple"
                                            mt={2}
                                            onClick={() => {
                                                setDescModalTitle(tuto.title);
                                                setDescModalContent(tuto.description);
                                                onDescOpen();
                                            }}
                                        >
                                            {t("Voir plus")}
                                        </Button>
                                    )}
                                </CardBody>
                                <CardFooter pt={0} p={4}>
                                    <Button
                                        leftIcon={<Icon as={FaPlay} />}
                                        colorScheme="purple"
                                        borderRadius="lg"
                                        size="sm"
                                        w="100%"
                                        onClick={() => handleWatchVideo(tuto.youtube_link, tuto.title)}
                                    >
                                        {t("Regarder")}
                                    </Button>
                                </CardFooter>
                            </Card>
                        );
                    })}
                </Flex>
                {totalPages > 1 && (
                    <Flex justify="center" mt={4} gap={2} align="center">
                        {Array.from({ length: totalPages }).map((_, idx) => (
                            <Box
                                key={idx}
                                w={currentPage === idx ? "24px" : "8px"}
                                h="8px"
                                bg={currentPage === idx ? "purple.500" : "gray.300"}
                                borderRadius="full"
                                transition="all 0.3s"
                                cursor="pointer"
                                onClick={() => scrollToPage(idx)}
                            />
                        ))}
                    </Flex>
                )}
            </AccordionPanel>
        </AccordionItem>
    );
};

export const Tutoriels = () => {
    const { t } = useTranslation();
    const [loadingData, setLoadingData] = useState(true);
    const navigate = useNavigate();

    // Search and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(1000); // Fetch all/many to group them properly
    const [selectedCategoryVal, setSelectedCategoryVal] = useState('ALL');
    const [paginationData, setPaginationData] = useState(null);

    // List data & active video modal
    const [data, setData] = useState([]);
    const [orderedCategories, setOrderedCategories] = useState([]); // [{nom, ordre}]
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [activeVideoId, setActiveVideoId] = useState(null);
    const [activeVideoTitle, setActiveVideoTitle] = useState('');

    // Description Modal disclosure & content state
    const { isOpen: isDescOpen, onOpen: onDescOpen, onClose: onDescClose } = useDisclosure();
    const [descModalTitle, setDescModalTitle] = useState('');
    const [descModalContent, setDescModalContent] = useState('');

    const getYouTubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Load data — fetch ordered category list and all tutoriels in parallel
    const fetchTutoriels = useCallback((signal) => {
        setLoadingData(true);
        Promise.all([
            axiosClient.get('/tutoriels/get_categories/', { signal }),
            axiosClient.get(`/tutoriels/getAllTutoriels/?page_size=${pageSize}&page=${currentPage + 1}&search_term=${debouncedSearchTerm}`, { signal }),
        ])
            .then(([catRes, tutoRes]) => {
                setOrderedCategories(catRes.data || []);
                setPaginationData(tutoRes.data);
                setData(tutoRes.data.results || []);
                setLoadingData(false);
            })
            .catch((error) => {
                if (error.name !== 'CanceledError' && error.message !== 'canceled') {
                    setLoadingData(false);
                }
            });
    }, [pageSize, currentPage, debouncedSearchTerm]);

    useEffect(() => {
        const abortController = new AbortController();
        fetchTutoriels(abortController.signal);
        return () => abortController.abort();
    }, [fetchTutoriels]);

    const handleWatchVideo = (url, title) => {
        const videoId = getYouTubeId(url);
        if (videoId) {
            setActiveVideoId(videoId);
            setActiveVideoTitle(title);
            onOpen();
        } else {
            // Fallback: open in new tab if regex parsing failed
            window.open(url, '_blank');
        }
    };

    const handleCloseModal = () => {
        setActiveVideoId(null);
        setActiveVideoTitle('');
        onClose();
    };

    // Colors
    const cardBg = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const cardInnerBg = useColorModeValue('gray.50', 'gray.700');

    // Select colors
    const selectBg = useColorModeValue('white', 'var(--chakra-colors-gray-700)');
    const selectColor = useColorModeValue('black', 'white');
    const selectBorder = useColorModeValue('var(--chakra-colors-gray-200)', 'var(--chakra-colors-gray-600)');
    const selectHoverBorder = useColorModeValue('var(--chakra-colors-gray-300)', 'var(--chakra-colors-gray-500)');
    const optionFocusBg = useColorModeValue('var(--chakra-colors-gray-100)', 'var(--chakra-colors-gray-600)');
    const optionActiveBg = useColorModeValue('var(--chakra-colors-purple-500)', 'var(--chakra-colors-purple-300)');

    const selectMenuShadow = useColorModeValue('0 4px 6px rgba(0,0,0,0.1)', '0 4px 6px rgba(0,0,0,0.5)');

    const reactSelectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: selectBg,
            borderColor: selectBorder,
            borderRadius: '12px',
            color: selectColor,
            '&:hover': { borderColor: selectHoverBorder },
            boxShadow: 'none',
            minHeight: '40px'
        }),
        menu: (base) => ({ 
            ...base, 
            backgroundColor: selectBg, 
            zIndex: 9999,
            boxShadow: selectMenuShadow
        }),
        menuList: (base) => ({
            ...base,
            backgroundColor: selectBg,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? optionFocusBg : 'transparent',
            color: selectColor,
            '&:active': { backgroundColor: optionActiveBg, color: 'white' },
            cursor: 'pointer'
        }),
        singleValue: (base) => ({ ...base, color: selectColor }),
        input: (base) => ({ ...base, color: selectColor }),
        placeholder: (base) => ({ ...base, color: 'var(--chakra-colors-gray-500)' }),
        indicatorSeparator: (base) => ({ ...base, backgroundColor: selectBorder }),
        dropdownIndicator: (base) => ({ 
            ...base, 
            color: selectBorder, 
            '&:hover': { color: selectColor } 
        }),
        clearIndicator: (base) => ({ 
            ...base, 
            color: selectBorder, 
            '&:hover': { color: selectColor } 
        }),
    };

    // Group tutorials by category, respecting the order from CategorieOrdre (via get_categories)
    const groupedMap = {}; // lower_key -> {display, tutos[]}
    data.forEach(tuto => {
        const rawCat = (tuto.categorie || t('Général')).trim();
        const key = rawCat.toLowerCase();
        if (!groupedMap[key]) groupedMap[key] = { display: rawCat, tutos: [] };
        groupedMap[key].tutos.push(tuto);
    });

    // Build groupedData following orderedCategories, then append any unknown at the end
    const groupedData = {};
    const seenKeys = new Set();

    // 1. Known categories in their persisted order
    orderedCategories.forEach(({ nom }) => {
        const key = nom.trim().toLowerCase();
        if (groupedMap[key]) {
            groupedData[groupedMap[key].display] = groupedMap[key].tutos;
            seenKeys.add(key);
        }
    });

    // 2. Categories not yet in orderedCategories (newly added, never reordered)
    Object.entries(groupedMap).forEach(([key, { display, tutos }]) => {
        if (!seenKeys.has(key)) {
            groupedData[display] = tutos;
        }
    });

    const categoryOptions = [
        { value: 'ALL', label: t("Toutes les catégories") },
        ...Object.keys(groupedData).map(cat => ({ value: cat, label: cat }))
    ];
    const currentCategoryOption = categoryOptions.find(opt => opt.value === selectedCategoryVal) || categoryOptions[0];

    return (
        <Box position="relative" mb={5}>
            {/* Breadcrumbs */}
            <Flex>
                <Box w="100%" display="flex" justifyContent="space-between" flexDirection={{ base: 'column', md: "row" }} mt={2} mb={{ base: 0, md: 5 }}>
                    <Breadcrumb fontStyle={"normal"} fontSize={{ base: "15px", sm: "16px", lg: "17px" }} color={"gray.400"}>
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{t('Tutoriels')}</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Box>
            </Flex>

            {/* Filter / Search header */}
            <Flex
                mb={6}
                p={4}
                bg={useColorModeValue('white', 'gray.700')}
                rounded="2xl"
                align="center"
                justify="space-between"
                shadow="sm"
                direction={{ base: 'column', md: 'row' }}
                gap={4}
            >
                <Text fontSize="lg" fontWeight="bold">
                    {t("Apprendre à utiliser Ecolinke")}
                </Text>
                <Flex gap={4} w={{ base: '100%', md: 'auto' }} direction={{ base: 'column', md: 'row' }}>
                    <Box w={{ base: '100%', md: '250px' }}>
                        <Select
                            options={categoryOptions}
                            value={currentCategoryOption}
                            onChange={(option) => setSelectedCategoryVal(option.value)}
                            styles={reactSelectStyles}
                            placeholder={t("Filtrer par catégorie")}
                            isSearchable
                        />
                    </Box>
                    <InputGroup size="md" w={{ base: '100%', md: "300px" }}>
                        <InputLeftElement pointerEvents='none'>
                            <SearchIcon color='gray.300' />
                        </InputLeftElement>
                        <Input
                            borderRadius="xl"
                            placeholder={t("Recherche...")}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(0);
                            }}
                        />
                    </InputGroup>
                </Flex>
            </Flex>

            {/* Accordion of Tutorial categories */}
            {loadingData ? (
                <Stack spacing={4}>
                    {[1, 2, 3].map((i) => (
                        <Box key={i} p={4} bg={cardBg} borderRadius="2xl" shadow="sm">
                            <Skeleton height="24px" width="70%" mb={2} />
                        </Box>
                    ))}
                </Stack>
            ) : data.length > 0 ? (
                <Stack spacing={6}>
                    {(() => {
                        const categoriesArray = Object.entries(groupedData).filter(([cat, tutos]) => {
                            // Exclude empty categories & apply category filter
                            if (!tutos || tutos.length === 0) return false;
                            return selectedCategoryVal === 'ALL' || cat === selectedCategoryVal;
                        });

                        const defaultIndices = categoriesArray.map((_, i) => i);

                        return (
                            <Accordion allowMultiple w="100%" defaultIndex={defaultIndices}>
                                {categoriesArray.map(([category, tutos], catIndex) => {
                                    const hoverBg = cardBg === "white" ? "gray.50" : "gray.700";

                                    return (
                                        <CategorySection
                                            key={catIndex}
                                            category={category}
                                            tutos={tutos}
                                            cardBg={cardBg}
                                            hoverBg={hoverBg}
                                            cardInnerBg={cardInnerBg}
                                            textColor={textColor}
                                            handleWatchVideo={handleWatchVideo}
                                            t={t}
                                            getYouTubeId={getYouTubeId}
                                            setDescModalTitle={setDescModalTitle}
                                            setDescModalContent={setDescModalContent}
                                            onDescOpen={onDescOpen}
                                        />
                                    );
                                })}
                            </Accordion>
                        );
                    })()}
                </Stack>
            ) : (
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    p={10}
                    bg={cardBg}
                    borderRadius="2xl"
                    shadow="sm"
                >
                    <Text fontSize="lg" color="gray.500" mb={2}>
                        {t("Aucun tutoriel disponible")}
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        {t("Les vidéos explicatives apparaîtront ici dès que l'administrateur les publiera.")}
                    </Text>
                </Flex>
            )}

            {/* Video Modal Player */}
            <Modal isOpen={isOpen} onClose={handleCloseModal} size="4xl" isCentered>
                <ModalOverlay />
                <ModalContent bg="black" borderRadius="2xl" overflow="hidden" m={4}>
                    <ModalCloseButton color="white" zIndex={10} bg="blackAlpha.600" borderRadius="full" _hover={{ bg: 'blackAlpha.800' }} />
                    <ModalBody p={0}>
                        {activeVideoId && (
                            <AspectRatio maxW="100%" ratio={16 / 9}>
                                <iframe
                                    title={activeVideoTitle}
                                    src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&rel=0`}
                                    allowFullScreen
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                />
                            </AspectRatio>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Description Modal */}
            <Modal isOpen={isDescOpen} onClose={onDescClose} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent borderRadius="2xl" p={2}>
                    <ModalHeader fontSize="xl" fontWeight="bold" borderBottomWidth="1px" borderColor={useColorModeValue('gray.100', 'gray.700')}>
                        {descModalTitle}
                    </ModalHeader>
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody py={4}>
                        <Text fontSize="md" color={textColor} whiteSpace="pre-wrap">
                            {descModalContent}
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="purple" borderRadius="xl" onClick={onDescClose}>
                            {t("Fermer")}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

const CirclePlayButton = () => {
    return (
        <Flex
            h={14}
            w={14}
            bg="white"
            borderRadius="full"
            align="center"
            justify="center"
            shadow="lg"
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.1)' }}
        >
            <Icon as={FaPlay} color="purple.600" w={5} h={5} ml={1} />
        </Flex>
    );
};

export default Tutoriels;
